import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Save, Trash2, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "", color: "#f5efe4", type: "retro_phone", status: "active",
  image_url: "", buffer_days_before: 0, buffer_days_after: 0,
};

export default function AdminDevices() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // device id being edited or 'new'
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/devices`, { withCredentials: true });
      setDevices(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(empty); };
  const startEdit = (d) => { setEditing(d.id); setForm({ ...d, image_url: d.image_url || "" }); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Adj meg nevet"); return; }
    try {
      const payload = {
        name: form.name,
        color: form.color,
        type: form.type,
        status: form.status,
        image_url: form.image_url || null,
        buffer_days_before: Number(form.buffer_days_before) || 0,
        buffer_days_after: Number(form.buffer_days_after) || 0,
      };
      if (editing === "new") {
        await axios.post(`${API}/admin/devices`, payload, { withCredentials: true });
        toast.success("Eszköz létrehozva");
      } else {
        await axios.patch(`${API}/admin/devices/${editing}`, payload, { withCredentials: true });
        toast.success("Eszköz frissítve");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mentés sikertelen");
    }
  };

  const removeDevice = async (id) => {
    if (!window.confirm("Biztosan inaktiválod ezt az eszközt? Új foglalások már nem érkeznek rá.")) return;
    await axios.delete(`${API}/admin/devices/${id}`, { withCredentials: true });
    toast.success("Eszköz inaktiválva");
    load();
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 800_000) { toast.error("Túl nagy kép, max 800KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image_url: reader.result }));
    reader.readAsDataURL(file);
  };

  return (
    <div data-testid="admin-devices-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl text-[var(--ink)]">Eszközök</h1>
        <button onClick={startNew} data-testid="new-device-btn" className="tk-btn-primary !py-2.5"><Plus size={14} />Új eszköz</button>
      </div>

      {editing && (
        <div data-testid="device-editor" className="tk-card p-6 mb-6 grid md:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs uppercase tracking-widest text-[var(--ink-soft)] mb-2">Név</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="device-name" className="tk-input" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-[var(--ink-soft)] mb-2">Szín (HEX)</label>
            <div className="flex gap-2">
              <input type="color" value={form.color || "#f5efe4"} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-12 h-11 rounded-md border border-[rgba(44,31,26,0.18)] bg-white" />
              <input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="tk-input flex-1" />
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-[var(--ink-soft)] mb-2">Státusz</label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="device-status" className="tk-input">
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-widest text-[var(--ink-soft)] mb-2">Buffer days before</label>
              <input type="number" min="0" value={form.buffer_days_before} onChange={(e) => setForm({ ...form, buffer_days_before: e.target.value })} className="tk-input" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-[var(--ink-soft)] mb-2">Buffer days after</label>
              <input type="number" min="0" value={form.buffer_days_after} onChange={(e) => setForm({ ...form, buffer_days_after: e.target.value })} className="tk-input" />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-widest text-[var(--ink-soft)] mb-2">Kép (URL vagy feltöltés)</label>
            <div className="flex gap-2 items-start">
              <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://... vagy data URI" className="tk-input flex-1" />
              <label className="tk-btn-secondary cursor-pointer !py-2.5">Feltöltés<input type="file" accept="image/*" onChange={onFile} className="hidden" /></label>
            </div>
            {form.image_url && <img src={form.image_url} alt="" className="mt-3 max-h-32 rounded-lg border border-[rgba(110,30,43,0.08)]" />}
          </div>
          <div className="md:col-span-2 flex gap-3 justify-end">
            <button onClick={() => setEditing(null)} className="tk-btn-secondary">Mégse</button>
            <button onClick={save} data-testid="save-device" className="tk-btn-primary"><Save size={14} />Mentés</button>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {loading && <p className="text-[var(--ink-soft)] italic">Betöltés…</p>}
        {!loading && devices.map((d) => (
          <div key={d.id} data-testid={`device-card-row-${d.id}`} className="tk-card p-5">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-10 h-10 rounded-full border border-[rgba(44,31,26,0.18)] flex items-center justify-center" style={{ background: d.color || "#f5efe4" }}>
                <Phone size={15} className={d.color === "#1f1a18" || d.color === "#6e1e2b" ? "text-white" : "text-[var(--burgundy)]"} />
              </span>
              <div>
                <div className="font-serif text-lg">{d.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--ink-soft)]">{d.status} · ±{d.buffer_days_before}/{d.buffer_days_after}d</div>
              </div>
            </div>
            {d.image_url && <img src={d.image_url} alt="" className="rounded-lg w-full h-32 object-cover mb-3" />}
            <div className="flex gap-2 mt-2">
              <button onClick={() => startEdit(d)} data-testid={`edit-device-${d.id}`} className="tk-btn-secondary !py-2 text-xs flex-1">Szerkesztés</button>
              <button onClick={() => removeDevice(d.id)} className="px-3 py-2 rounded-full border border-[rgba(110,30,43,0.25)] text-[var(--burgundy)] hover:bg-[rgba(110,30,43,0.06)]"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
